import React, { forwardRef } from 'react';

import type { TypographyProps } from '@mui/material';
import { alpha, Typography } from '@mui/material';
import clsx from 'clsx';
import { FileText } from 'lucide-react';
import { IconColors } from 'src/components/icons/icon-utils';
import { LabelChipIcon } from 'src/components/icons/LabelChipIcon';
import { SmallPartyIcon } from 'src/components/icons/SmallPartyIcon';
import { SmallXIcon } from 'src/components/icons/SmallXIcon';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  isClickable: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { isClickable }) => ({
  chip: {
    '&:hover': {
      backgroundColor: isClickable ? alpha(IconColors.DarkGrey, 0.05) : undefined,
    },
    alignItems: 'center',
    border: theme.appBorders.outline,
    borderRadius: 8,
    cursor: isClickable ? 'pointer' : undefined,
    display: 'flex',
    gap: theme.spacing(0.5),
    maxWidth: '100%',
    // Needed so chip shrinks and shows ... when chip overflows container.
    overflow: 'hidden',
    padding: theme.spacing(0.25, 0.5),
    width: 'fit-content',
  },
  chipText: {
    fontSize: 12,
  },
  deleteIcon: {
    '&:hover': {
      backgroundColor: alpha(IconColors.DarkGrey, 0.75),
    },
    alignItems: 'center',
    backgroundColor: alpha(IconColors.DarkGrey, 0.5),
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0.25),
  },
}));

interface AlineChipProps {
  chipText: string;
  className?: string;
  disabled?: boolean;
  labelTypographyProps?: TypographyProps;
  onClick?: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
  variant?: 'label' | 'party' | 'document';
}

export const AlineChip = forwardRef(function AlineChip(
  {
    chipText: labelText,
    className,
    disabled,
    labelTypographyProps,
    onClick,
    onDelete,
    variant = 'label',
  }: AlineChipProps,
  ref: React.Ref<HTMLDivElement>
) {
  const { classes } = useStyles({
    isClickable: !disabled && Boolean(onClick),
  });

  return (
    <div
      ref={ref}
      className={clsx(classes.chip, className)}
      onClick={e => {
        if (!disabled && onClick) {
          e.stopPropagation();
          onClick(e);
        }
      }}
    >
      <AlineChipIcon variant={variant} />
      <Typography noWrap className={classes.chipText} {...labelTypographyProps}>
        {labelText}
      </Typography>
      {onDelete && (
        <div className={classes.deleteIcon} onClick={onDelete}>
          <SmallXIcon fill={IconColors.White} />
        </div>
      )}
    </div>
  );
});

function AlineChipIcon({ variant }: { variant: NonNullable<AlineChipProps['variant']> }) {
  switch (variant) {
    case 'label':
      return <LabelChipIcon />;
    case 'party':
      return <SmallPartyIcon />;
    case 'document':
      return <FileText size={16} />;
  }
}
